import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { formatShortDate } from '@/lib/format';

type ProbeStatus = 'ok' | 'degraded' | 'error' | 'not_configured';

interface Probe {
  status: ProbeStatus;
  durationMs: number;
  detail?: string;
}

interface StatusResponse {
  service: string;
  summary: 'ok' | 'degraded';
  durationMs: number;
  probes: {
    db: Probe;
    identity: Probe;
    serviceBus: Probe;
    keyVault: Probe;
    storage: Probe;
  };
  timestamp: string;
}

/**
 * Admin health dashboard. Polls /status on a 15s loop and renders the probe
 * matrix. Auto-pause on tab hide so we don't keep hitting the API when the
 * dashboard is in a background tab.
 */
export function AdminHealth() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiFetch<StatusResponse>('/status', { anonymous: true })
      .then((r) => setData(r))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    let timer: number | null = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 15_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  return (
    <div className="min-h-screen p-6 max-w-3xl mx-auto">
      <header className="border-b border-border pb-4 mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Platform health</h1>
          <p className="text-sm text-muted-foreground">
            Polls /status every 15s. Anonymous endpoint; APIM exempts it from JWT validation.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-sm border border-border rounded-md disabled:opacity-60"
        >
          {loading ? 'Checking…' : 'Refresh'}
        </button>
      </header>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {!data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div
            className={`mb-4 rounded-md px-4 py-3 ${
              data.summary === 'ok'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border border-amber-200 text-amber-800'
            }`}
          >
            <div className="text-sm">
              Overall: <strong>{data.summary}</strong> · {data.durationMs}ms ·{' '}
              {formatShortDate(data.timestamp)}
            </div>
          </div>
          <ul className="space-y-2 text-sm">
            <ProbeRow label="Database (SQL Hyperscale)" probe={data.probes.db} />
            <ProbeRow label="Identity-svc JWKS" probe={data.probes.identity} />
            <ProbeRow label="Service Bus DNS" probe={data.probes.serviceBus} />
            <ProbeRow label="Key Vault DNS" probe={data.probes.keyVault} />
            <ProbeRow label="Storage account DNS" probe={data.probes.storage} />
          </ul>
        </>
      )}
    </div>
  );
}

function ProbeRow({ label, probe }: { label: string; probe: Probe }) {
  const tone =
    probe.status === 'ok'
      ? 'text-emerald-700'
      : probe.status === 'not_configured'
        ? 'text-muted-foreground'
        : 'text-destructive';
  return (
    <li className="flex items-center justify-between border border-border rounded-md px-3 py-2">
      <span>{label}</span>
      <span className={`text-xs ${tone}`}>
        {probe.status} · {probe.durationMs}ms
        {probe.detail ? ` — ${probe.detail}` : ''}
      </span>
    </li>
  );
}
